import { useEffect, useState, useCallback } from 'react';
import { Hourglass, Phone, RefreshCw } from 'lucide-react';
import axios from 'axios';
import PlayerNameLink from '../../components/PlayerNameLink';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Admin-side roster of every player whose referral payout is still pending.
 * Sorted by who's closest to the load threshold so the admin can prioritize
 * a nudge call/text to the players almost there.
 */
export const PendingReferralsPanel = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/referrals/pending`, { withCredentials: true });
            setItems(data.pending || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    return (
        <div className="card-dark p-6" data-testid="admin-pending-referrals">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Hourglass className="w-5 h-5 text-[#FF6B00]" />
                    Pending Referrals ({items.length})
                </h3>
                <button
                    type="button"
                    onClick={fetchItems}
                    disabled={loading}
                    className="text-[#A0AAB2] hover:text-white p-1.5 rounded-md hover:bg-white/5"
                    title="Refresh"
                    data-testid="pending-referrals-refresh"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>
            <p className="text-xs text-[#A0AAB2] mb-3">
                Friends who signed up via referral but haven't loaded enough yet — sorted by closest to threshold.
                A quick text from you can convert these.
            </p>

            {loading && items.length === 0 ? (
                <p className="text-sm text-[#A0AAB2] py-6 text-center">Loading…</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-[#A0AAB2] py-6 text-center">No pending referrals — every signup has loaded to threshold or there are none.</p>
            ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                    {items.map((r, i) => (
                        <div key={i} className="bg-[#0a0a0a] rounded-lg p-3 border border-white/5" data-testid={`pending-row-${i}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0 flex-1">
                                    <p className="text-white font-bold text-sm truncate">
                                        <PlayerNameLink id={r.referee_id} name={r.referee_name} testId={`pending-referee-link-${i}`} />
                                        <span className="text-[#A0AAB2] font-normal text-xs ml-2">
                                            ← invited by <PlayerNameLink id={r.referrer_id} name={r.referrer_name} className="text-[#A0AAB2] text-xs" testId={`pending-referrer-link-${i}`} />
                                        </span>
                                    </p>
                                    {(r.referee_phone || r.referee_email) && (
                                        <p className="text-[10px] text-[#A0AAB2] truncate">
                                            {r.referee_phone && (
                                                <a
                                                    href={`sms:${r.referee_phone}?body=${encodeURIComponent(`Hey ${r.referee_name}, you're $${r.remaining.toFixed(0)} away from unlocking your $${(r.referee_bonus || 0).toFixed(0)} BETohalics signup bonus!`)}`}
                                                    className="text-[#39FF14] hover:underline inline-flex items-center gap-1"
                                                    data-testid={`pending-sms-${i}`}
                                                >
                                                    <Phone className="w-3 h-3" /> {r.referee_phone}
                                                </a>
                                            )}
                                            {r.referee_email && r.referee_phone && <span className="mx-1">·</span>}
                                            {r.referee_email && <span>{r.referee_email}</span>}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right text-xs font-mono shrink-0">
                                    <span className="text-[#FFD700] font-bold">${(r.remaining || 0).toFixed(0)}</span>
                                    <span className="text-[#A0AAB2]"> to go</span>
                                </div>
                            </div>
                            <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-[#FF6B00] to-[#39FF14] transition-all"
                                    style={{ width: `${r.progress_pct || 0}%` }}
                                />
                            </div>
                            <p className="text-[10px] text-[#A0AAB2] mt-1 tabular-nums">
                                ${(r.loaded_progress || 0).toFixed(0)} / ${(r.load_threshold || 0).toFixed(0)} loaded · unlocks <span className="text-[#39FF14]">+${(r.referrer_bonus || 0).toFixed(0)}</span> / <span className="text-[#00FFFF]">+${(r.referee_bonus || 0).toFixed(0)}</span>
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
